import { Entry, FlowiseFields, useStore, TextFieldWithSources, CitationSource, TextWithSources, useClientSettingStore } from '@assets/model/Store';
import { observer } from 'mobx-react';

import { CompanyCompareUtils } from '@assets/model/CompanyCompareUtils';
import { useNavUtils } from '@assets/utils/NavUtils';
import { ChartCompareCompanies } from '../Charts/ChartCompareCompanies';
import { ChartScoreBreakdown } from '../Charts/ChartScoreBreakdown';
import { DealDetailsHeader } from '../DealDetails/DealDetailsHeader/DealDetailsHeader';
import { Panel } from '../Panel/Panel';
import { PanelCompanyOverview } from '../Panel/PanelCompanyOverview/PanelCompanyOverview';
import { Flex } from '../UI/Flex/Flex';

import { ChartCompanyRevenue } from '../Charts/ChartCompanyRevenue';
import { ScrollPanel } from '../Panel/ScrollPanel/ScrollPanel';
import * as style from './DealDashboard.module.less';
import classNames from 'classnames';
import we_like from './we_like.svg';
import key_risk from './key_risks.svg';
import due_diligence from './due_diligence.svg';
import { flow } from 'lodash';

const GAP_SIZE = 15;

// Helper function to strip all citations and markdown links from text
const stripCitationsAndMarkdown = (text: string): string => {
    if (!text) return text;

    let cleanText = text;

    // Remove superscript citations (¹²³⁴⁵⁶⁷⁸⁹⁰)
    cleanText = cleanText.replace(/[\u00B9\u00B2\u00B3\u2074\u2075\u2076\u2077\u2078\u2079\u2070]+/g, '');

    // Remove bracket citations [1], [2], etc.
    cleanText = cleanText.replace(/\[\d+\]/g, '');

    // Remove markdown links [Text](url) -> Text
    cleanText = cleanText.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');

    // Remove standalone "Source" word at the end of sentences or text
    cleanText = cleanText.replace(/\.\s*Source\s*$/gi, '.');
    cleanText = cleanText.replace(/\s+Source\s*$/gi, '');

    return cleanText.trim();
};

export const DealDashboard = observer(() => {
    const store = useStore();
    const clientStore = useClientSettingStore();
    const navUtils = useNavUtils();
    const reportId = store.selectedReportId;
    const compareList = CompanyCompareUtils.findCompaniesToCompare(reportId, 4);
    const do_we_have_pdf = store.query.report_has_pdf;
    const enableSourcing = clientStore.enableSourcing;
    const showPotentialCustomers = clientStore.showPotentialCustomers;
    const flowise_filled: FlowiseFields = {
        opportunity: null,
        business_overview: null,
        market_description: null,
        market_size: null,

        core_product_services: null,
        key_customers: null,
        potential_customers: null,
        competitor_segments: null,
        what_we_like: null,
        key_risks: null,
        diligence: null
    };
    var flowise = store.report_flowise ?? flowise_filled;

    const output_list_generic = (
        list: Entry[],
        num_per_row = 4,
        className: string = '',
        gapSize: number,
        createElement: (data: Entry) => JSX.Element,
        noDataMessage?: string
    ) => {
        if (!list || list.length === 0) {
            return <NoDataElement message={noDataMessage} />;
        }
        return (
            <>
                <Flex
                    wrap="wrap"
                    style={{
                        width: '100%',
                        gap: `${gapSize}px`,
                        rowGap: `${gapSize}px`,
                        paddingBottom: '20px'
                    }}
                    className={className}
                >
                    {list.map((item, i) => createElement(item))}
                </Flex>
            </>
        );
    };
    const output_boxes = (list: Entry[], num_per_row = 4) => {
        return output_list_generic(
            list,
            num_per_row,
            '',
            GAP_SIZE,
            (e: Entry) => {
                return (
                    <div
                        style={{
                            width: `calc(${100 / num_per_row}% - ${GAP_SIZE}px)`
                        }}
                        key={e.title}
                        className={classNames(
                            style.box_list_entry,
                            style.box_container
                        )}
                    >
                        <div className="title">{e.title}</div>
                        {e.description}
                    </div>
                );
            }
        );
    };

    const output_boxes_with_links = (list: Entry[], num_per_row = 1, noDataMessage?: string) => {
        return output_list_generic(
            list,
            num_per_row,
            '',
            GAP_SIZE,
            (e: Entry) => {
                // If sourcing is disabled, strip all markdown and don't show Source button
                if (!enableSourcing) {
                    const cleanDescription = stripCitationsAndMarkdown(e.description);
                    return (
                        <div
                            style={{
                                width: `calc(${100 / num_per_row}% - ${GAP_SIZE}px)`
                            }}
                            key={e.title}
                            className={classNames(
                                style.box_list_entry,
                                style.box_container
                            )}
                        >
                            <div className="title">{e.title}</div>
                            <div>{cleanDescription}</div>
                        </div>
                    );
                }

                // Extract link and description text from markdown format [description](url)
                const extractLinkData = (text: string) => {
                    if (!text) return { cleanText: '', link: null };

                    const linkMatch = text.match(/\[([^\]]+)\]\(([^\)]+)\)/);
                    if (linkMatch) {
                        // Remove the entire markdown link [text](url) from the description
                        const cleanText = text.replace(/\[([^\]]+)\]\(([^\)]+)\)/, '').trim();
                        return {
                            cleanText: cleanText,
                            link: linkMatch[2]        // URL inside (...) is the source link
                        };
                    }
                    return { cleanText: text, link: null };
                };

                const { cleanText, link } = extractLinkData(e.description);

                return (
                    <div
                        style={{
                            width: `calc(${100 / num_per_row}% - ${GAP_SIZE}px)`
                        }}
                        key={e.title}
                        className={classNames(
                            style.box_list_entry,
                            style.box_container
                        )}
                    >
                        <div className="title">{e.title}</div>
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                            <div style={{ flex: 1 }}>{cleanText}</div>
                            {link && (
                                <a
                                    href={link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={style.sourceLink}
                                    title="View source"
                                >
                                    <svg
                                        width="14"
                                        height="14"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        style={{ marginRight: '4px' }}
                                    >
                                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                                        <polyline points="15 3 21 3 21 9" />
                                        <line x1="10" y1="14" x2="21" y2="3" />
                                    </svg>
                                    Source
                                </a>
                            )}
                        </div>
                    </div>
                );
            },
            noDataMessage
        );
    };

    // Helper to render text with inline citations (for list items like What We Like, Key Risks)
    const renderTextWithInlineCitations = (item: any) => {
        if (!item.description) return null;

        // If sourcing is disabled, strip citations and render plain text
        if (!enableSourcing) {
            return <div>{stripCitationsAndMarkdown(item.description)}</div>;
        }

        // Check if item has sources (new format with citations)
        if (item.sources && Array.isArray(item.sources)) {
            const description = item.description;
            const sources = item.sources;

            // Split text by citation markers and create clickable citations
            const parts: (string | JSX.Element)[] = [];
            const citationRegex = /[\u00B9\u00B2\u00B3\u2074\u2075\u2076\u2077\u2078\u2079\u2070]+/g;
            let lastIndex = 0;
            let match;

            while ((match = citationRegex.exec(description)) !== null) {
                // Add text before citation
                if (match.index > lastIndex) {
                    parts.push(description.substring(lastIndex, match.index));
                }

                // Convert superscript back to number
                const superToNum: { [key: string]: string } = {
                    '\u00B9': '1', '\u00B2': '2', '\u00B3': '3', '\u2074': '4', '\u2075': '5',
                    '\u2076': '6', '\u2077': '7', '\u2078': '8', '\u2079': '9', '\u2070': '0'
                };
                const numStr = match[0].split('').map(c => superToNum[c] || c).join('');
                const sourceId = parseInt(numStr);
                const source = sources.find((s: any) => s.id === sourceId);

                // Add clickable citation
                if (source && source.url) {
                    parts.push(
                        <a
                            key={`citation-${sourceId}-${match.index}`}
                            href={source.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={style.citationNumber}
                            title={source.name}
                        >
                            {match[0]}
                        </a>
                    );
                } else {
                    parts.push(
                        <span key={`citation-${sourceId}-${match.index}`} className={style.citationNumber}>
                            {match[0]}
                        </span>
                    );
                }

                lastIndex = match.index + match[0].length;
            }

            // Add remaining text
            if (lastIndex < description.length) {
                parts.push(description.substring(lastIndex));
            }

            return (
                <>
                    <div>{parts.length > 0 ? parts : description}</div>

                    {/* Sources list - same pattern as TextWithCitations */}
                    {sources.length > 0 && (
                        <div className={style.sourcesSection} style={{ marginTop: '12px' }}>
                            <div className={style.sourcesTitle}>Sources</div>
                            <div className={style.sourcesList}>
                                {sources.map((source: any) => (
                                    <div key={source.id} className={style.sourceItem}>
                                        <span className={style.sourceNumber}>{toSuperscript(source.id)}</span>
                                        {source.url ? (
                                            <a
                                                href={source.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className={style.sourceText}
                                            >
                                                {source.name}
                                            </a>
                                        ) : (
                                            <span className={style.sourceTextNoLink}>{source.name}</span>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </>
            );
        }

        // Old format - plain text
        return <>{item.description}</>;
    };

    const output_we_like = (list: Entry[], iconSrc: string) => {
        const num_per_row = 1;
        const gapsize = 5;
        return output_list_generic(
            list,
            num_per_row,
            style.box_container,
            gapsize,
            (e: Entry) => {
                return (
                    <div
                        style={{
                            width: `calc(${100 / num_per_row}% - ${gapsize}px)`,
                            padding: '5px'
                        }}
                        key={e.title}
                        className={style.box_list_entry}
                    >
                        <Flex direction="column">
                            <Flex
                                style={{ gap: `${gapsize}px` }}
                                alignItems="center"
                            >
                                <img
                                    src={iconSrc}
                                    alt="We Like"
                                    style={{
                                        paddingRight: '10px',
                                        transform: 'translateY(1px)'
                                    }}
                                />
                                <div
                                    className="text"
                                    style={{ fontSize: '18px' }}
                                >
                                    {e.title}
                                </div>
                            </Flex>
                            <div
                                className="text"
                                style={{ paddingLeft: '35px' }}
                            >
                                {renderTextWithInlineCitations(e)}
                            </div>
                        </Flex>
                    </div>
                );
            }
        );
    };

    const parseDiligenceDescription = (text: string) => {
        if (!text) return null;

        const detailedMatch = text.match(/\*\*\[DETAILED\]\*\*(.*?)(?=\*\*\[|$)/s);
        const relevanceMatch = text.match(/\*\*\[RELEVANCE\]\*\*(.*?)(?=\*\*\[|$)/s);
        const believesMatch = text.match(/\*\*\[BELIEVES\]\*\*(.*?)(?=\*\*\[|$)/s);

        return {
            detailed: detailedMatch?.[1]?.trim() || '',
            relevance: relevanceMatch?.[1]?.trim() || '',
            believes: believesMatch?.[1]?.trim() || ''
        };
    };

    const output_due_diligence = (list: Entry[]) => {
        const num_per_row = 2;
        return output_list_generic(
            list,
            num_per_row,
            '',
            GAP_SIZE,
            (e: Entry) => {
                const sections = parseDiligenceDescription(e.description);

                // If no sections found, fallback to original rendering
                if (!sections || (!sections.detailed && !sections.relevance && !sections.believes)) {
                    return (
                        <div
                            style={{
                                width: `calc(${100 / num_per_row}% - 10px)`,
                                padding: '20px'
                            }}
                            key={e.title}
                            className={classNames(
                                style.box_list_entry,
                                style.box_container
                            )}
                        >
                            <Flex style={{ gap: '20px' }} alignItems="center">
                                <img src={due_diligence} alt="Due Diligence" className={style.dueDiligenceIcon} />
                                <Flex direction="column">
                                    <div className="title">{e.title}</div>
                                    <div className="text">{e.description}</div>
                                </Flex>
                            </Flex>
                        </div>
                    );
                }

                // New structured rendering with sections
                return (
                    <div
                        style={{
                            width: `calc(${100 / num_per_row}% - 10px)`,
                            padding: '20px'
                        }}
                        key={e.title}
                        className={classNames(
                            style.box_list_entry,
                            style.box_container
                        )}
                    >
                        <Flex style={{ gap: '15px' }} alignItems="flex-start">
                            <img src={due_diligence} alt="Due Diligence" className={style.dueDiligenceIcon} style={{ marginTop: '2px' }} />
                            <Flex direction="column" style={{ flex: 1, gap: '12px' }}>
                                <div className="title">{e.title}</div>

                                {sections.believes && (
                                    <div>
                                        <div style={{ fontWeight: 600, marginBottom: '4px', fontSize: '14px' }}>
                                            Need-to-Believe:
                                        </div>
                                        <div className="text">{renderTextWithInlineCitations({ description: sections.believes, sources: (e as any).sources })}</div>
                                    </div>
                                )}

                                {sections.relevance && (
                                    <div>
                                        <div style={{ fontWeight: 600, marginBottom: '4px', fontSize: '14px' }}>
                                            Deal Relevance:
                                        </div>
                                        <div className="text">{renderTextWithInlineCitations({ description: sections.relevance, sources: (e as any).sources })}</div>
                                    </div>
                                )}

                                {sections.detailed && (
                                    <div>
                                        <div style={{ fontWeight: 600, marginBottom: '4px', fontSize: '14px' }}>
                                            Diligence Objective:
                                        </div>
                                        <div className="text">{renderTextWithInlineCitations({ description: sections.detailed, sources: (e as any).sources })}</div>
                                    </div>
                                )}
                            </Flex>
                        </Flex>
                    </div>
                );
            }
        );
    };

    const NoDataElement = (props?: { message?: string }) => {
        const defaultMessage = "No Data Available";
        return <div className={style.noData}>{props?.message || defaultMessage}</div>;
    };

    const NoDataWrapper = (text: string) => {
        if (!text || text.trim().length === 0) {
            return <NoDataElement />;
        }
        return text;
    };

    // Superscript characters for citations
    const superscriptDigits: { [key: number]: string } = {
        1: '\u00B9', 2: '\u00B2', 3: '\u00B3', 4: '\u2074', 5: '\u2075',
        6: '\u2076', 7: '\u2077', 8: '\u2078', 9: '\u2079', 0: '\u2070'
    };

    const toSuperscript = (num: number): string => {
        return num.toString().split('').map(d => superscriptDigits[parseInt(d)] || d).join('');
    };

    // Parse text field with sources (backward compatible: string or object)
    const parseTextWithSources = (data: TextFieldWithSources): { text: string; sources: CitationSource[] } => {
        if (!data) {
            return { text: '', sources: [] };
        }
        // Old format: plain string
        if (typeof data === 'string') {
            const text = enableSourcing ? data : stripCitationsAndMarkdown(data);
            return { text, sources: [] };
        }
        // New format: object with text and sources
        const text = data.text || '';
        const sources = data.sources || [];

        // If sourcing is disabled, strip all citations and return empty sources
        if (!enableSourcing) {
            return { text: stripCitationsAndMarkdown(text), sources: [] };
        }

        return { text, sources };
    };

    // Component to render text with inline citations (generic, works for any text field)
    const TextWithCitations = ({ data }: { data: TextFieldWithSources }) => {
        const { text, sources } = parseTextWithSources(data);

        if (!text || text.trim().length === 0) {
            return <NoDataElement />;
        }

        // If no sources, render plain text (backward compatible)
        if (sources.length === 0) {
            return <div>{text}</div>;
        }

        // Render text with clickable citation numbers
        const renderTextWithCitations = () => {
            // Replace citation markers (¹²³ etc) with clickable spans
            const parts: (string | JSX.Element)[] = [];
            let lastIndex = 0;

            // Match superscript numbers in text (¹ ² ³ etc or regular digits after special markers)
            const citationRegex = /[\u00B9\u00B2\u00B3\u2074\u2075\u2076\u2077\u2078\u2079\u2070]+/g;
            let match;

            while ((match = citationRegex.exec(text)) !== null) {
                // Add text before citation
                if (match.index > lastIndex) {
                    parts.push(text.substring(lastIndex, match.index));
                }

                // Convert superscript back to number to find source
                const superToNum: { [key: string]: string } = {
                    '\u00B9': '1', '\u00B2': '2', '\u00B3': '3', '\u2074': '4', '\u2075': '5',
                    '\u2076': '6', '\u2077': '7', '\u2078': '8', '\u2079': '9', '\u2070': '0'
                };
                const numStr = match[0].split('').map(c => superToNum[c] || c).join('');
                const sourceId = parseInt(numStr);
                const source = sources.find(s => s.id === sourceId);

                // Add clickable citation
                if (source && source.url) {
                    parts.push(
                        <a
                            key={`citation-${sourceId}-${match.index}`}
                            href={source.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={style.citationNumber}
                            title={source.name}
                        >
                            {match[0]}
                        </a>
                    );
                } else {
                    parts.push(
                        <span key={`citation-${sourceId}-${match.index}`} className={style.citationNumber}>
                            {match[0]}
                        </span>
                    );
                }

                lastIndex = match.index + match[0].length;
            }

            // Add remaining text
            if (lastIndex < text.length) {
                parts.push(text.substring(lastIndex));
            }

            return parts.length > 0 ? parts : text;
        };

        return (
            <div>
                <div>
                    {renderTextWithCitations()}
                </div>

                {/* Sources list */}
                <div className={style.sourcesSection}>
                    <div className={style.sourcesTitle}>Sources</div>
                    <div className={style.sourcesList}>
                        {sources.map((source) => (
                            <div key={source.id} className={style.sourceItem}>
                                <span className={style.sourceNumber}>{toSuperscript(source.id)}</span>
                                {source.url ? (
                                    <a
                                        href={source.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={style.sourceText}
                                    >
                                        {source.name}
                                    </a>
                                ) : (
                                    <span className={style.sourceTextNoLink}>{source.name}</span>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    };

    const lineTitle = (title: string) => {
        return <div className={style.title}>{title}</div>;
    };

    const lineTitleWithInfo = (title: string, tooltipText: string) => {
        return (
            <div className={style.title}>
                {title}
                <span className={style.infoIconWrapper}>
                    <svg
                        className={style.infoIcon}
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                    >
                        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" fill="none" />
                        <text
                            x="12"
                            y="17"
                            textAnchor="middle"
                            fontSize="14"
                            fontWeight="600"
                            fill="currentColor"
                            fontFamily="Arial, sans-serif"
                        >
                            i
                        </text>
                    </svg>
                    <div className={style.tooltip}>{tooltipText}</div>
                </span>
            </div>
        );
    };

    return (
        <Flex direction="column" fillCell={true}>
            {do_we_have_pdf && <DealDetailsHeader />}
            <ScrollPanel>
                <Flex
                    direction="row"
                    style={{ width: '100%', minHeight: '450px' }}
                >
                    <Flex direction="column" style={{ flex: 1 }}>
                        {lineTitle('Summary of the Opportunity')}
                        <Panel className={style.panelPaddingRight}>
                            <TextWithCitations data={flowise.opportunity} />
                        </Panel>
                        <Panel className={style.panelPaddingRight}>
                            {lineTitle('Business Overview')}
                            <Panel className={style.panelPaddingRight}>
                                {flowise.business_overview ? (
                                    <TextWithCitations data={flowise.business_overview} />
                                ) : (
                                    store.companyOverview
                                )}
                            </Panel>
                        </Panel>
                    </Flex>
                    {do_we_have_pdf && (
                        <Panel
                            className={style.panelPaddingLeft}
                            style={{ flex: 1 }}
                        >
                            <ChartCompanyRevenue reportId={reportId} />
                        </Panel>
                    )}
                </Flex>
                <Flex
                    direction="row"
                    style={{
                        width: '100%',
                        minHeight: '200px'
                    }}
                >
                    <Flex direction="column">
                        {lineTitle('Core Products / Services')}
                        {output_boxes_with_links(flowise.core_product_services, 4)}
                    </Flex>
                </Flex>
                <Flex
                    direction="row"
                    style={{ width: '100%', minHeight: '450px' }}
                >
                    <Panel className={showPotentialCustomers ? style.panelPaddingRight : ''}>
                        {lineTitleWithInfo(
                            'Key Customers',
                            "Key Customers are companies confirmed as clients through direct, verifiable evidence found during research. Valid sources include customer testimonials on the target company's website, press releases naming specific customers, news articles about partnerships or contracts, and published case studies. Each customer listed has been validated with a cited source."
                        )}
                        <div>{output_boxes_with_links(
                            flowise.key_customers,
                            1,
                            "No confirmed customers found in public sources. Verification requires published testimonials, case studies, or press releases."
                        )}</div>
                    </Panel>
                    {showPotentialCustomers && (
                        <Panel className={style.panelPaddingRight}>
                            {lineTitleWithInfo(
                                'Potential Customers',
                                "Potential Customers are high-probability prospects identified via market and competitive analysis. We identify them by finding key customers of direct competitors, pinpointing companies in high-demand market segments, or determining a strategic fit based on industry trends. The source for each prospect cites the specific public data, like a competitor's report or market analysis, that supports the identification."
                            )}
                            <div>{output_boxes_with_links(
                                flowise.potential_customers,
                                1,
                                "No prospects identified through public research. Identification requires competitor data, market reports, or industry analysis available online."
                            )}</div>
                        </Panel>
                    )}
                </Flex>
                {do_we_have_pdf && (
                    <Flex direction="row" style={{ width: '100%' }}>
                        <Panel
                            className={classNames(
                                style.panelPaddingRight,
                                style.minHeight
                            )}
                        >
                            <ChartScoreBreakdown reportId={reportId} />
                        </Panel>
                        <Panel
                            className={classNames(
                                style.panelPaddingRight,
                                style.minHeight
                            )}
                        >
                            <ChartCompareCompanies
                                reportId={reportId}
                                companies={compareList}
                            />
                        </Panel>
                    </Flex>
                )}
                <Flex
                    direction="row"
                    style={{ width: '100%', minHeight: '450px' }}
                >
                    <Panel className={style.panelPaddingRight}>
                        {lineTitle('Market Description')}
                        <TextWithCitations data={flowise.market_description} />
                    </Panel>
                    <Panel className={style.panelPaddingRight}>
                        {lineTitle('Market Size & Growth')}
                        <TextWithCitations data={flowise.market_size} />
                    </Panel>
                </Flex>
                <Flex
                    direction="row"
                    style={{
                        width: '100%',
                        minHeight: '200px'
                    }}
                >
                    <Flex direction="column">
                        {lineTitle('Competitor Segments')}
                        {output_boxes_with_links(flowise.competitor_segments, 4)}
                    </Flex>
                </Flex>
                <Flex
                    direction="row"
                    style={{ width: '100%', paddingBottom: '20px' }}
                >
                    <Panel className={style.panelPaddingRight}>
                        {lineTitle('What We Like')}
                        {output_we_like(flowise.what_we_like, we_like)}
                    </Panel>
                    <Panel className={style.panelPaddingRight}>
                        {lineTitle('Key Risks')}
                        {output_we_like(flowise.key_risks, key_risk)}
                    </Panel>
                </Flex>
                <Flex
                    direction="row"
                    style={{
                        width: '100%',
                        minHeight: '200px'
                    }}
                >
                    <Flex direction="column">
                        {lineTitle('Focus Areas for Due Diligence')}
                        {output_due_diligence(flowise.diligence)}
                    </Flex>
                </Flex>
            </ScrollPanel>
        </Flex>
    );
});
